import { useCallback, useEffect, useReducer, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "app/store";
import {
  setPendingAttachmentToken,
  setPendingPrompt,
} from "features/assistant/slice";
import { takePendingAttachmentFile } from "features/assistant/pendingAttachment";
import { AssistantWebSocket, ServerEvent } from "services/assistant/websocket";
import {
  useGenerateChatUrlMutation,
  useLazyListSessionsQuery,
  useCreateAgentAttachmentUploadUrlMutation,
  AgentSessionSummary,
} from "services/assistant/endpoints";
import { useSelectedTeam } from "features/team-settings/hooks";
import { chatReducer, initialChatState } from "./chatReducer";
import { convertHistoryMessages } from "./convertHistoryMessages";
import { attachmentChipText } from "./attachmentChip";
import { useResumeOnReviewCompletion } from "./useResumeOnReviewCompletion";

// Matches the service model's @range(max) on CreateAgentAttachmentUploadUrl (500 MiB).
const MAX_ATTACHMENT_BYTES = 524288000;

export interface UseAssistantChatOptions {
  alwaysActive?: boolean;
}

export const useAssistantChat = (options?: UseAssistantChatOptions) => {
  const { alwaysActive = false } = options ?? {};
  const { isAssistantFileAttachmentEnabled } = useSelectedTeam();
  const headerSelectedTeam = useAppSelector(
    (state) => state.audits.headerSelectedTeam,
  );
  const isOpen = useAppSelector((s) => s.assistant.isOpen);
  // Optional URL override for local testing — set in settings.json to bypass generateChatUrl
  const assistantUrlOverride = useAppSelector(
    (state) => (state.settings as any)?.endpoints?.assistantUrlOverride,
  );

  const [chatState, chatDispatch] = useReducer(chatReducer, initialChatState);
  const [sessions, setSessions] = useState<AgentSessionSummary[]>([]);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(
    null,
  );
  const selectedSessionIdRef = useRef<string | null>(null);

  const wsRef = useRef<AssistantWebSocket | null>(null);
  const hasConnectedRef = useRef(false);
  const connectionAttemptRef = useRef(0);
  const connectionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const historyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const uploadInFlightRef = useRef(false);
  // A staged file is bound to the session it was picked in: performUpload resolves the session
  // at confirm time, so without this a session switch would upload it into the wrong conversation.
  const pendingAttachmentRef = useRef<{
    file: File;
    sessionId: string;
  } | null>(null);

  const [generateChatUrl] = useGenerateChatUrlMutation();
  const [fetchSessions] = useLazyListSessionsQuery();
  const [createAttachmentUploadUrl] =
    useCreateAgentAttachmentUploadUrlMutation();
  const [isUploadingAttachment, setIsUploadingAttachment] = useState(false);
  // Reviews the agent asked to be notified about once the user has finished with them.
  const [pendingReviewIds, setPendingReviewIds] = useState<string[]>([]);
  const [pendingAttachmentConfirmation, setPendingAttachmentConfirmation] =
    useState<File | null>(null);

  // Sync team changes with the agent
  useEffect(() => {
    if (headerSelectedTeam && wsRef.current?.isConnected) {
      wsRef.current.sendUserStateSnapshot();
    }
  }, [headerSelectedTeam]);

  // Fetch sessions when sidebar opens (or immediately when alwaysActive)
  useEffect(() => {
    if (!isOpen && !alwaysActive) return;
    const loadSessions = async () => {
      try {
        const result = await fetchSessions({
          agentName: "gaia_assistant",
          teamName: headerSelectedTeam,
          limit: 20,
        }).unwrap();
        setSessions(result.sessions);
      } catch (err) {
        console.error("Failed to load sessions:", err);
      }
    };
    loadSessions();
  }, [isOpen, alwaysActive, fetchSessions, headerSelectedTeam]);

  const handleServerEvent = useCallback((event: ServerEvent) => {
    switch (event.type) {
      case "TEXT_START":
        chatDispatch({ type: "SET_LOADING", loading: true });
        // Seed the message's source (badge/accent shows while streaming); citations arrive later.
        chatDispatch({
          type: "SET_PENDING_MESSAGE_ID",
          messageId: event.message_id,
          sourceMetadata: event.source_metadata,
        });
        break;
      case "TEXT_DELTA":
        chatDispatch({
          type: "APPEND_DELTA",
          messageId: event.message_id,
          delta: event.delta,
        });
        break;
      case "TEXT_END":
        // source_metadata carries the resolved citations/references (empty on the opening
        // deltas); store it against the message id.
        chatDispatch({
          type: "MARK_COMPLETE",
          messageId: event.message_id,
          sourceMetadata: event.source_metadata,
        });
        chatDispatch({ type: "SET_LOADING", loading: false });
        break;
      case "TOOL_CONFIRMATION_REQUEST":
        chatDispatch({
          type: "SET_PENDING_CONFIRMATION",
          confirmation: {
            requestId: event.request_id,
            toolName: event.tool_name,
            description: event.description,
          },
        });
        break;
      case "MESSAGE_HISTORY_SNAPSHOT":
        if (historyTimeoutRef.current) {
          clearTimeout(historyTimeoutRef.current);
          historyTimeoutRef.current = null;
        }
        {
          const { items, messageMeta } = convertHistoryMessages(event.messages);
          chatDispatch({ type: "SET_ITEMS", items, messageMeta });
        }
        {
          const fb = event.session_metadata?.feedback;
          chatDispatch({
            type: "SET_SESSION_FEEDBACK",
            feedback: fb === "THUMBS_UP" || fb === "THUMBS_DOWN" ? fb : null,
          });
        }
        break;
      case "TOOL_CALL_START":
        chatDispatch({
          type: "ADD_TOOL_CALL",
          toolUseId: event.tool_use_id,
          toolName: event.tool_name,
        });
        break;
      case "TOOL_CALL_END":
        chatDispatch({
          type: "COMPLETE_TOOL_CALL",
          toolUseId: event.tool_use_id,
          status: event.status,
        });
        break;
      case "ERROR":
        chatDispatch({ type: "SET_INLINE_ERROR", message: event.message });
        chatDispatch({ type: "SET_LOADING", loading: false });
        break;
      case "STEP":
        chatDispatch({
          type: "APPEND_STEP",
          messageId: event.message_id,
          stepName: event.step_name,
          status: event.status === "in_progress" ? "inProgress" : event.status,
        });
        break;
      case "AWAIT_AGENT_ACTION_REVIEW":
        setPendingReviewIds((ids) =>
          ids.includes(event.review_id) ? ids : [...ids, event.review_id],
        );
        break;
      default:
        break;
    }
  }, []);

  const handleConnectionChange = useCallback((connected: boolean) => {
    chatDispatch({ type: "SET_CONNECTED", connected });
    if (connected) {
      chatDispatch({ type: "SET_ERROR", error: null });
      if (connectionTimeoutRef.current) {
        clearTimeout(connectionTimeoutRef.current);
        connectionTimeoutRef.current = null;
      }
      if (selectedSessionIdRef.current && wsRef.current) {
        wsRef.current.sendRequestMessageHistory();
        chatDispatch({ type: "SET_LOADING_HISTORY", loading: true });

        historyTimeoutRef.current = setTimeout(() => {
          console.error("History snapshot timed out after 15s");
          chatDispatch({ type: "SET_LOADING_HISTORY", loading: false });
          chatDispatch({
            type: "SET_ERROR",
            error: "Something went wrong.",
          });
        }, 15000);
      }
    } else {
      hasConnectedRef.current = false;
      if (historyTimeoutRef.current) {
        clearTimeout(historyTimeoutRef.current);
        historyTimeoutRef.current = null;
      }
      // Drop any file awaiting confirmation: its session is gone, so close the modal rather
      // than leave it staged and blocking the next pick.
      pendingAttachmentRef.current = null;
      setPendingAttachmentConfirmation(null);
      chatDispatch({ type: "CLEAR_MESSAGES" });
      chatDispatch({
        type: "SET_ERROR",
        error: "Connection lost. Try to reconnect?",
      });
    }
  }, []);

  const connectToWebSocket = useCallback(
    async (teamName: string, sessionId?: string | null) => {
      const attempt = ++connectionAttemptRef.current;
      wsRef.current?.disconnect();
      wsRef.current = null;
      if (connectionTimeoutRef.current) {
        clearTimeout(connectionTimeoutRef.current);
        connectionTimeoutRef.current = null;
      }
      if (historyTimeoutRef.current) {
        clearTimeout(historyTimeoutRef.current);
        historyTimeoutRef.current = null;
      }
      chatDispatch({ type: "RESET" });
      try {
        let url: string;
        if (assistantUrlOverride) {
          if (sessionId) {
            url = `${assistantUrlOverride}?sessionId=${sessionId}`;
          } else {
            const newSessionId = crypto.randomUUID();
            url = `${assistantUrlOverride}?sessionId=${newSessionId}`;
            setSelectedSessionId(newSessionId);
            selectedSessionIdRef.current = newSessionId;
          }
        } else {
          const result = await generateChatUrl({
            agentName: "gaia_assistant",
            protocolType: "WEBSOCKET",
            teamName: teamName,
            ...(sessionId ? { sessionId } : {}),
          }).unwrap();
          url = result.url;

          // Bail out if a newer connection attempt started while we were awaiting
          if (attempt !== connectionAttemptRef.current) return;

          if (!sessionId && result.sessionId) {
            setSelectedSessionId(result.sessionId);
            selectedSessionIdRef.current = result.sessionId;
          }
        }

        if (attempt !== connectionAttemptRef.current) return;

        const ws = new AssistantWebSocket(
          handleServerEvent,
          handleConnectionChange,
        );
        ws.connect(url);
        wsRef.current = ws;
        hasConnectedRef.current = true;

        connectionTimeoutRef.current = setTimeout(() => {
          if (!ws.isConnected) {
            ws.disconnect();
            chatDispatch({
              type: "SET_ERROR",
              error: "Connection timed out. Please try again.",
            });
            chatDispatch({ type: "SET_CONNECTED", connected: false });
          }
        }, 10000);
      } catch (err) {
        if (attempt !== connectionAttemptRef.current) return;
        console.error("Failed to connect:", err);
        chatDispatch({
          type: "SET_ERROR",
          error: "Failed to connect. Please try again.",
        });
        chatDispatch({ type: "SET_CONNECTED", connected: false });
      }
    },
    [
      generateChatUrl,
      handleServerEvent,
      handleConnectionChange,
      assistantUrlOverride,
    ],
  );

  // Initial connection
  useEffect(() => {
    if ((!isOpen && !alwaysActive) || !headerSelectedTeam) return;
    if (hasConnectedRef.current && wsRef.current) return;
    connectToWebSocket(headerSelectedTeam);
  }, [isOpen, alwaysActive, connectToWebSocket, headerSelectedTeam]);

  // Send pending prompt (e.g. from "Analyze with AI" button) once connected
  const pendingPrompt = useAppSelector((s) => s.assistant.pendingPrompt);
  const reduxDispatch = useAppDispatch();

  useEffect(() => {
    if (!pendingPrompt || !chatState.isConnected || !wsRef.current?.isConnected)
      return;
    chatDispatch({
      type: "ADD_MESSAGE",
      id: crypto.randomUUID(),
      role: "user",
      content: pendingPrompt.prompt,
      displayContent: pendingPrompt.displayText,
      isStreaming: false,
    });
    wsRef.current.sendMessage(pendingPrompt.prompt, pendingPrompt.displayText);
    reduxDispatch(setPendingPrompt(null));
  }, [pendingPrompt, chatState.isConnected, reduxDispatch]);

  // Resume the conversation once the user has finished a review the agent is waiting on.
  const handleReviewCompleted = useCallback((reviewId: string) => {
    if (!wsRef.current?.isConnected) return;
    wsRef.current.sendAgentActionReviewCompleted(reviewId);
    setPendingReviewIds((ids) => ids.filter((id) => id !== reviewId));
  }, []);

  useResumeOnReviewCompletion({
    pendingReviewIds,
    onCompleted: handleReviewCompleted,
    isConnected: chatState.isConnected,
  });

  const performUpload = useCallback(
    async (file: File) => {
      const ws = wsRef.current;
      const sessionId = selectedSessionIdRef.current;
      if (!ws?.isConnected || !sessionId || !headerSelectedTeam) {
        chatDispatch({
          type: "SET_INLINE_ERROR",
          message:
            "Couldn't attach the file — the assistant isn't connected yet. Try again in a moment.",
        });
        return;
      }
      const fileName = file.name.split(/[/\\]/).pop() || file.name;

      uploadInFlightRef.current = true;
      setIsUploadingAttachment(true);
      try {
        const { url, attachmentId, bucketOwner } =
          await createAttachmentUploadUrl({
            fileName,
            sessionId,
            teamName: headerSelectedTeam,
            size: file.size,
          }).unwrap();

        const putResponse = await fetch(url, {
          method: "PUT",
          body: file,
          headers: { "x-amz-expected-bucket-owner": bucketOwner },
        });
        if (!putResponse.ok) {
          throw new Error(`Upload failed with status ${putResponse.status}`);
        }

        chatDispatch({
          type: "ADD_MESSAGE",
          id: crypto.randomUUID(),
          role: "user",
          content: attachmentChipText(fileName),
          isStreaming: false,
        });
        ws.sendFileAttachmentUploaded(attachmentId);
      } catch (err) {
        console.error("Failed to upload attachment:", err);
        chatDispatch({
          type: "SET_INLINE_ERROR",
          message: `Failed to upload "${fileName}". Please try again.`,
        });
      } finally {
        uploadInFlightRef.current = false;
        setIsUploadingAttachment(false);
      }
    },
    [createAttachmentUploadUrl, headerSelectedTeam],
  );

  // Every upload entry point (sidebar picker/drop, home-page picker/drop via pendingAttachment)
  // funnels through here, so the disclaimer gate can't be bypassed. Cheap checks run first so a
  // file that is about to be rejected outright never raises a confirmation.
  const uploadAttachment = useCallback(
    async (file: File) => {
      const ws = wsRef.current;
      const sessionId = selectedSessionIdRef.current;
      if (!ws?.isConnected || !sessionId || !headerSelectedTeam) {
        chatDispatch({
          type: "SET_INLINE_ERROR",
          message:
            "Couldn't attach the file — the assistant isn't connected yet. Try again in a moment.",
        });
        return;
      }
      if (uploadInFlightRef.current || pendingAttachmentRef.current) {
        chatDispatch({
          type: "SET_INLINE_ERROR",
          message: "Please wait for the current upload to finish.",
        });
        return;
      }

      const fileName = file.name.split(/[/\\]/).pop() || file.name;
      if (file.size < 1 || file.size > MAX_ATTACHMENT_BYTES) {
        chatDispatch({
          type: "SET_INLINE_ERROR",
          message: `"${fileName}" is too large. The maximum attachment size is 500 MiB.`,
        });
        return;
      }

      pendingAttachmentRef.current = { file, sessionId };
      setPendingAttachmentConfirmation(file);
    },
    [headerSelectedTeam],
  );

  const cancelAttachment = useCallback(() => {
    pendingAttachmentRef.current = null;
    setPendingAttachmentConfirmation(null);
  }, []);

  const confirmAttachment = useCallback(() => {
    const staged = pendingAttachmentRef.current;
    pendingAttachmentRef.current = null;
    setPendingAttachmentConfirmation(null);
    if (!staged) return;
    if (staged.sessionId !== selectedSessionIdRef.current) {
      chatDispatch({
        type: "SET_INLINE_ERROR",
        message: `The conversation changed, so "${staged.file.name}" wasn't attached. Attach it again to add it here.`,
      });
      return;
    }
    void performUpload(staged.file);
  }, [performUpload]);

  const pendingAttachmentToken = useAppSelector(
    (s) => s.assistant.pendingAttachmentToken,
  );
  useEffect(() => {
    if (
      pendingAttachmentToken === null ||
      !chatState.isConnected ||
      !wsRef.current?.isConnected ||
      !selectedSessionId ||
      !headerSelectedTeam
    )
      return;
    const file = takePendingAttachmentFile();
    reduxDispatch(setPendingAttachmentToken(null));
    if (file) uploadAttachment(file);
  }, [
    pendingAttachmentToken,
    chatState.isConnected,
    selectedSessionId,
    headerSelectedTeam,
    reduxDispatch,
    uploadAttachment,
  ]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      wsRef.current?.disconnect();
      wsRef.current = null;
      hasConnectedRef.current = false;
      if (connectionTimeoutRef.current) {
        clearTimeout(connectionTimeoutRef.current);
        connectionTimeoutRef.current = null;
      }
      if (historyTimeoutRef.current) {
        clearTimeout(historyTimeoutRef.current);
        historyTimeoutRef.current = null;
      }
    };
  }, []);

  const sendMessage = useCallback(
    (text: string): boolean => {
      const trimmed = text.trim();
      if (!trimmed || !wsRef.current?.isConnected) return false;

      if (chatState.pendingConfirmation) {
        wsRef.current.sendToolConfirmationResponse(
          chatState.pendingConfirmation.requestId,
          false,
        );
        chatDispatch({ type: "SET_PENDING_CONFIRMATION", confirmation: null });
      }

      chatDispatch({
        type: "ADD_MESSAGE",
        id: crypto.randomUUID(),
        role: "user",
        content: trimmed,
        isStreaming: false,
      });
      wsRef.current.sendMessage(trimmed);
      return true;
    },
    [chatState.pendingConfirmation],
  );

  const handleConfirmation = useCallback(
    (confirmed: boolean) => {
      if (!chatState.pendingConfirmation || !wsRef.current?.isConnected) return;
      wsRef.current.sendToolConfirmationResponse(
        chatState.pendingConfirmation.requestId,
        confirmed,
      );
      chatDispatch({ type: "SET_PENDING_CONFIRMATION", confirmation: null });
    },
    [chatState.pendingConfirmation],
  );

  const handleSessionChange = useCallback(
    (sessionId: string | null) => {
      // Same reasoning as the disconnect path: a staged file belongs to the conversation it was
      // picked in, so drop it rather than leave the modal open to dead-end on confirm.
      pendingAttachmentRef.current = null;
      setPendingAttachmentConfirmation(null);
      setSelectedSessionId(sessionId);
      selectedSessionIdRef.current = sessionId;
      hasConnectedRef.current = false;
      connectToWebSocket(headerSelectedTeam, sessionId);
    },
    [connectToWebSocket, headerSelectedTeam],
  );

  const retry = useCallback(() => {
    chatDispatch({ type: "SET_ERROR", error: null });
    hasConnectedRef.current = false;
    connectToWebSocket(headerSelectedTeam, selectedSessionId);
  }, [connectToWebSocket, headerSelectedTeam, selectedSessionId]);

  const refreshSessions = useCallback(async () => {
    try {
      const result = await fetchSessions({
        agentName: "gaia_assistant",
        teamName: headerSelectedTeam,
        limit: 20,
      }).unwrap();
      setSessions(result.sessions);
    } catch (err) {
      console.error("Failed to refresh sessions:", err);
    }
  }, [fetchSessions, headerSelectedTeam]);

  const interruptResponse = useCallback(() => {
    wsRef.current?.sendInterruptResponse();
  }, []);

  const sendFeedback = useCallback((feedback: "THUMBS_UP" | "THUMBS_DOWN") => {
    wsRef.current?.sendFeedback(feedback);
    chatDispatch({ type: "SET_SESSION_FEEDBACK", feedback });
  }, []);

  const dismissError = useCallback(() => {
    chatDispatch({ type: "SET_INLINE_ERROR", message: null });
  }, []);

  return {
    // State
    items: chatState.items,
    messageMeta: chatState.messageMeta,
    messageSteps: chatState.messageSteps,
    isConnected: chatState.isConnected,
    isLoading: chatState.isLoading,
    isLoadingHistory: chatState.isLoadingHistory,
    error: chatState.error,
    inlineError: chatState.inlineError,
    pendingConfirmation: chatState.pendingConfirmation,
    sessionFeedback: chatState.sessionFeedback,
    sessions,
    selectedSessionId,
    isAssistantFileAttachmentEnabled,
    isUploadingAttachment,
    pendingAttachmentConfirmation,
    confirmAttachment,
    cancelAttachment,

    // Actions
    sendMessage,
    uploadAttachment,
    sendFeedback,
    interruptResponse,
    handleConfirmation,
    handleSessionChange,
    refreshSessions,
    retry,
    dismissError,
  };
};
